import type { CollectionValue } from "../../bedrock";

import { z } from "zod";

import {
  integrationAllowedScopesSchema,
  integrationConnectionIdSchema,
  integrationScopeKeySchema,
} from "../../agentIntegration";
import {
  assetFindResponseSchema,
  assetOrderBySchema,
  assetTypeSchema,
  assetUpdateRequestSchema,
  assetUpdateResponseSchema,
} from "../../asset";
import {
  bedrockOrderEventSchema,
  fulfillmentOrderResponseSchema,
  fulfillmentResponseSchema,
  orderDetailResponseSchema,
  orderEventListResponseSchema,
  orderListResponseSchema,
  refundResponseSchema,
} from "../../bedrock";
import {
  BEDROCK_DATABASE_KEY_INPUT_MAX_LENGTH,
  BEDROCK_DATABASE_KEY_MAX_LENGTH,
  BEDROCK_DATABASE_KEY_NORMALIZED_PATTERN_MESSAGE,
  BEDROCK_DATABASE_KEY_PATTERN,
  BEDROCK_DATABASE_KEY_PATTERN_MESSAGE,
  normalizeBedrockDatabaseKey,
} from "../../bedrockDatabaseKeys";
import {
  contentArchiveEntryInputSchema,
  contentCreateEntryInputSchema,
  contentEntryCommandOutputSchema,
  contentReadEntryInputSchema,
  contentUpdateEntryInputSchema,
} from "../../contentEntryCommands";
import {
  addCustomDomainOutputSchema,
  deleteCustomDomainOutputSchema,
  findCustomDomainsOutputSchema,
  getCustomDomainOutputSchema,
  verifyCustomDomainOutputSchema,
} from "../../customDomain";
import { designDirectionRequestSchema } from "../../designDirection";
import { integrationTagSchema } from "../../integrationTag";
import { amountSchema } from "../../money";
import {
  orderFinancialStatusSchema,
  orderItemFulfillmentStatusSchema,
} from "../../order";
import { currencySchema } from "../../project";
import {
  registryContentFilesSchema,
  registryCreateItemInputSchema,
  registryCreateItemOutputSchema,
  registryItemTypeSchema,
  registryListItemsResultSchema,
  registrySlugSchema,
} from "../../registry";
import { userWorkspaceRoleSchema } from "../../routes/workspace";
import { taskSchema } from "../../tasks";
import { knownDataForSeoMcpToolMetadataRegistrySchema } from "./dataForSeoMcp";
import {
  mediaMcpDesignDirectionSetOutputSchema,
  mediaMcpGenerateImageInputSchema,
  mediaMcpGenerateJobOutputSchema,
  mediaMcpGenerateVideoInputSchema,
  mediaMcpGetGenerationStatusInputSchema,
  mediaMcpGetGenerationStatusOutputSchema,
} from "./mediaMcp";

const bedrockMcpReadKeySchema = z
  .string()
  .min(1)
  .max(BEDROCK_DATABASE_KEY_MAX_LENGTH)
  .regex(BEDROCK_DATABASE_KEY_PATTERN, {
    message: BEDROCK_DATABASE_KEY_PATTERN_MESSAGE,
  });

const bedrockMcpInputKeySchema = z
  .string()
  .min(1)
  .max(BEDROCK_DATABASE_KEY_INPUT_MAX_LENGTH)
  .transform(normalizeBedrockDatabaseKey)
  .pipe(
    z
      .string()
      .min(1)
      .max(BEDROCK_DATABASE_KEY_MAX_LENGTH)
      .regex(BEDROCK_DATABASE_KEY_PATTERN, {
        message: BEDROCK_DATABASE_KEY_NORMALIZED_PATTERN_MESSAGE,
      }),
  );

const bedrockMcpDisplayNameSchema = z.string().min(1).max(255);
const bedrockMcpRecordIdSchema = z.uuid();

const bedrockMcpCollectionFieldTypeSchema = z.enum([
  "string",
  "integer",
  "float",
  "boolean",
  "datetime",
  "json",
  "phone",
  "email",
]);

const bedrockMcpCollectionValueSchema: z.ZodType<CollectionValue> = z
  .lazy(() => {
    return z.union([
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
      z.array(bedrockMcpCollectionValueSchema),
      z.record(z.string(), bedrockMcpCollectionValueSchema),
    ]);
  })
  .describe("CollectionValue");

const bedrockMcpCollectionParamSchema = z.object({
  collectionKey: bedrockMcpInputKeySchema,
});

const bedrockMcpFieldParamSchema = z.object({
  collectionKey: bedrockMcpInputKeySchema,
  key: bedrockMcpInputKeySchema,
});

const bedrockMcpRecordParamSchema = z.object({
  collectionKey: bedrockMcpInputKeySchema,
  recordId: bedrockMcpRecordIdSchema,
});

const bedrockMcpCollectionFieldInputSchema = z.object({
  key: bedrockMcpInputKeySchema,
  displayName: bedrockMcpDisplayNameSchema,
  type: bedrockMcpCollectionFieldTypeSchema,
  nullable: z.boolean().optional(),
});

const bedrockMcpCollectionFieldOutputSchema = z.object({
  fieldId: z.uuid(),
  key: bedrockMcpReadKeySchema,
  displayName: bedrockMcpDisplayNameSchema,
  type: bedrockMcpCollectionFieldTypeSchema,
  nullable: z.boolean(),
});

const bedrockMcpCollectionOutputSchema = z.object({
  collectionId: z.uuid(),
  collectionKey: bedrockMcpReadKeySchema,
  displayName: bedrockMcpDisplayNameSchema,
  fields: z.array(bedrockMcpCollectionFieldOutputSchema),
});

const bedrockMcpRecordOutputSchema = z.object({
  recordId: bedrockMcpRecordIdSchema,
  collectionKey: bedrockMcpReadKeySchema,
  valuesByKey: z.record(z.string(), bedrockMcpCollectionValueSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const bedrockMcpGetCatalogInputSchema = z
  .object({})
  .describe("BedrockMcpGetCatalogInput");

export const bedrockMcpCreateCollectionInputSchema = z
  .object({
    collectionKey: bedrockMcpInputKeySchema,
    displayName: bedrockMcpDisplayNameSchema,
    fields: z.array(bedrockMcpCollectionFieldInputSchema),
  })
  .describe("BedrockMcpCreateCollectionInput");

export const bedrockMcpUpdateCollectionInputSchema =
  bedrockMcpCollectionParamSchema
    .extend({
      displayName: bedrockMcpDisplayNameSchema.optional(),
      newCollectionKey: bedrockMcpInputKeySchema.optional(),
    })
    .refine(
      (value) => {
        return (
          value.displayName !== undefined ||
          value.newCollectionKey !== undefined
        );
      },
      {
        message:
          "at least one of displayName / newCollectionKey must be provided",
      },
    )
    .describe("BedrockMcpUpdateCollectionInput");

export const bedrockMcpCreateCollectionFieldInputSchema =
  bedrockMcpCollectionParamSchema
    .extend(bedrockMcpCollectionFieldInputSchema.shape)
    .describe("BedrockMcpCreateCollectionFieldInput");

export const bedrockMcpUpdateCollectionFieldInputSchema =
  bedrockMcpFieldParamSchema
    .extend({
      displayName: bedrockMcpDisplayNameSchema.optional(),
      newKey: bedrockMcpInputKeySchema.optional(),
      nullable: z.boolean().optional(),
    })
    .refine(
      (value) => {
        return (
          value.displayName !== undefined ||
          value.newKey !== undefined ||
          value.nullable !== undefined
        );
      },
      { message: "at least one field attribute must be provided" },
    )
    .describe("BedrockMcpUpdateCollectionFieldInput");

export const bedrockMcpDeleteCollectionFieldInputSchema =
  bedrockMcpFieldParamSchema.describe("BedrockMcpDeleteCollectionFieldInput");

export const bedrockMcpCreateRecordInputSchema = bedrockMcpCollectionParamSchema
  .extend({
    valuesByKey: z.record(z.string(), bedrockMcpCollectionValueSchema),
  })
  .describe("BedrockMcpCreateRecordInput");

export const bedrockMcpUpdateRecordInputSchema = bedrockMcpRecordParamSchema
  .extend({
    valuesByKey: z.record(z.string(), bedrockMcpCollectionValueSchema),
  })
  .describe("BedrockMcpUpdateRecordInput");

export const bedrockMcpReadRecordInputSchema =
  bedrockMcpRecordParamSchema.describe("BedrockMcpReadRecordInput");

export const bedrockMcpQueryRecordsInputSchema = bedrockMcpCollectionParamSchema
  .extend({
    limit: z.number().int().min(1).max(500).optional(),
    offset: z.number().int().min(0).max(50_000).optional(),
    filter: z
      .array(
        z.object({
          key: bedrockMcpInputKeySchema,
          op: z.literal("equals"),
          value: bedrockMcpCollectionValueSchema,
        }),
      )
      .optional(),
  })
  .describe("BedrockMcpQueryRecordsInput");

export const bedrockMcpDeleteRecordInputSchema =
  bedrockMcpRecordParamSchema.describe("BedrockMcpDeleteRecordInput");

export const bedrockMcpCatalogOutputSchema = z
  .object({
    collections: z.array(bedrockMcpCollectionOutputSchema),
  })
  .describe("BedrockMcpCatalogOutput");

export const bedrockMcpCollectionEnvelopeOutputSchema = z
  .object({
    collection: bedrockMcpCollectionOutputSchema,
  })
  .describe("BedrockMcpCollectionEnvelopeOutput");

export const bedrockMcpRecordEnvelopeOutputSchema = z
  .object({
    record: bedrockMcpRecordOutputSchema,
  })
  .describe("BedrockMcpRecordEnvelopeOutput");

export const bedrockMcpRecordsEnvelopeOutputSchema = z
  .object({
    records: z.array(bedrockMcpRecordOutputSchema),
  })
  .describe("BedrockMcpRecordsEnvelopeOutput");

export const bedrockMcpDeleteRecordOutputSchema = z
  .object({
    recordId: bedrockMcpRecordIdSchema,
  })
  .describe("BedrockMcpDeleteRecordOutput");

export const bedrockMcpQueryAnalyticsInputSchema = z
  .object({
    query: z
      .string()
      .min(1)
      .describe(
        "A read-only ClickHouse SQL query to run against the project's Replo analytics data (page views, sessions, purchases, attribution, etc.). ",
      ),
    projectIdOverride: z
      .uuid()
      .optional()
      .describe(
        "Optional admin-only project ID whose analytics namespace should be queried instead of the authenticated project.",
      ),
  })
  .describe("BedrockMcpQueryAnalyticsInput");

export const bedrockMcpQueryAnalyticsOutputSchema = z
  .object({
    rowCount: z.number().int().describe("Number of rows returned."),
    data: z
      .array(z.record(z.string(), z.unknown()))
      .describe(
        "Result rows as an array of column-name → value objects. Column types follow ClickHouse `JSON` format conventions (numbers as strings for 64-bit integers, ISO-8601 strings for DateTimes, etc.).",
      ),
  })
  .describe("BedrockMcpQueryAnalyticsOutput");

const uploadAssetMetadataSchema = z.object({
  mimeType: z.string().optional(),
  name: z.string().min(1).optional(),
  altText: z.string().min(1).optional(),
  isIndexed: z.boolean().optional(),
  tags: z.array(z.string().min(1)).max(16).optional(),
  indexingHint: z.string().max(2000).optional(),
  generateEmbedding: z.boolean().optional(),
  // Optional caller-chosen storage key, relative to the project prefix, so
  // the asset URL carries a meaningful path instead of a random UUID — e.g.
  // "brands/studs/fonts/space-grotesk-500-normal" or "logo.png". Independent
  // of `name` (the library label). Sanitized and confined under
  // projects/{projectId}/ server-side; re-uploading the same key overwrites
  // the object. Omit for a collision-free UUID key.
  objectKey: z.string().min(1).max(512).optional(),
});

const assetUploadAssetInputSchema = z
  .object({
    // Caller-assigned primary id so an optimistic upload tile and the persisted
    // asset share one identity end to end. Omit to let the server mint one.
    id: z.uuid().optional(),
    r2Uri: z.string().optional(),
    url: z.url().optional(),
    dataBase64: z.string().optional(),
  })
  .extend(uploadAssetMetadataSchema.shape)
  .refine((value) => {
    return (
      [value.r2Uri, value.url, value.dataBase64].filter((source) => {
        return Boolean(source);
      }).length === 1
    );
  }, "Each asset must include exactly one source field");

const mcpUploadAssetInputSchema = z
  .object({
    r2Uri: z.string().optional(),
    url: z.url().optional(),
    dataBase64: z.string().optional(),
    name: uploadAssetMetadataSchema.shape.name,
    altText: uploadAssetMetadataSchema.shape.altText,
    tags: uploadAssetMetadataSchema.shape.tags,
  })
  .strict()
  .refine((value) => {
    return (
      [value.r2Uri, value.url, value.dataBase64].filter((source) => {
        return Boolean(source);
      }).length === 1
    );
  }, "Each asset must include exactly one source field");

export const assetUploadAssetsInputSchema = z
  .object({
    assets: z.array(assetUploadAssetInputSchema).min(1).max(10),
  })
  .describe("AssetUploadAssetsInput");

export const mcpUploadAssetsInputSchema = z
  .object({
    assets: z.array(mcpUploadAssetInputSchema).min(1).max(10),
  })
  .describe("McpUploadAssetsInput");

const assetUploadSuccessSchema = z.object({
  assetId: z.string().optional(),
  index: z.number(),
  kind: assetTypeSchema,
  mimeType: z.string(),
  name: z.string(),
  objectKey: z.string(),
  isIndexed: z.boolean(),
  sizeBytes: z.number(),
  success: z.literal(true),
  url: z.string(),
  tags: z.array(z.string()).optional(),
  altText: z.string().optional(),
  embeddingText: z.string().optional(),
  label: z.string().optional(),
  embeddingGenerated: z.boolean().optional(),
});

export const mcpUploadAssetsOutputSchema = z
  .object({
    failureCount: z.number(),
    results: z.array(
      z.discriminatedUnion("success", [
        assetUploadSuccessSchema,
        z.object({
          error: z.string(),
          index: z.number(),
          success: z.literal(false),
        }),
      ]),
    ),
    success: z.boolean(),
    successCount: z.number(),
    total: z.number(),
  })
  .describe("McpUploadAssetsOutput");

const mcpDownloadAssetSourceSchema = z.object({
  url: z.url(),
  integrationKey: z
    .string()
    .min(1)
    .optional()
    .describe(
      "Optional connected integration key, such as slack, for authenticated downloads. Omit for public URLs.",
    ),
  connectionId: integrationConnectionIdSchema
    .optional()
    .describe(
      "Replo integration connection ID that produced this URL. Include it when the provider has multiple eligible connections.",
    ),
});

const mcpDownloadAssetInputSchema = z
  .object({
    source: mcpDownloadAssetSourceSchema.describe(
      "Where to download the asset from. Set integrationKey for private Slack or other connected integration URLs so Replo can fetch through the user's connected integration without exposing tokens.",
    ),
    name: uploadAssetMetadataSchema.shape.name,
    altText: uploadAssetMetadataSchema.shape.altText,
    tags: uploadAssetMetadataSchema.shape.tags,
  })
  .strict();

export const mcpDownloadAssetsInputSchema = z
  .object({
    assets: z.array(mcpDownloadAssetInputSchema).min(1).max(10),
  })
  .describe("McpDownloadAssetsInput");

const mcpAssetJobStatusSchema = z.enum([
  "queued",
  "in_progress",
  "completed",
  "failed",
]);

const mcpAssetJobSchema = z.object({
  jobId: z.string(),
  status: mcpAssetJobStatusSchema,
  estimatedSecondsRemaining: z.number().nullable(),
  url: z.string().nullable(),
  error: z.string().optional(),
});

export const mcpDownloadAssetsOutputSchema = z
  .object({
    jobIds: z.array(z.string()),
  })
  .describe("McpDownloadAssetsOutput");

export const mcpUploadAssetsJobOutputSchema = z
  .object({
    jobIds: z.array(z.string()),
  })
  .describe("McpUploadAssetsJobOutput");

export const mcpGetAssetDownloadStatusInputSchema = z
  .object({
    jobIds: z.array(z.string().min(1)).min(1).max(10),
  })
  .describe("McpGetAssetDownloadStatusInput");

export const mcpGetAssetDownloadStatusOutputSchema = z
  .object({
    jobs: z.array(mcpAssetJobSchema),
  })
  .describe("McpGetAssetDownloadStatusOutput");

export const mcpGetAssetUploadStatusInputSchema = z
  .object({
    jobIds: z.array(z.string().min(1)).min(1).max(10),
  })
  .describe("McpGetAssetUploadStatusInput");

export const mcpGetAssetUploadStatusOutputSchema = z
  .object({
    jobs: z.array(mcpAssetJobSchema),
  })
  .describe("McpGetAssetUploadStatusOutput");

// (Yuxin, 2026-07-10, REPL-29159): one find verb — `q` searches by relevance, no `q` lists all.
export const mcpFindAssetsInputSchema = z
  .object({
    q: z
      .string()
      .max(200)
      .optional()
      .describe(
        "Search query. When set, assets come back in relevance order (semantic, tag, and name matching). Omit to list assets, sorted by orderBy.",
      ),
    ids: z
      .array(z.uuid())
      .min(1)
      .max(100)
      .optional()
      .describe("Restrict to these asset ids."),
    type: assetTypeSchema
      .optional()
      .describe("Restrict results to a single asset type."),
    tags: z
      .array(z.string().min(1))
      .max(16)
      .optional()
      .describe("Only return assets carrying all of these tags."),
    createdAtGte: z.iso
      .datetime({ offset: true })
      .optional()
      .describe("Only assets created at or after this ISO datetime."),
    createdAtLte: z.iso
      .datetime({ offset: true })
      .optional()
      .describe("Only assets created at or before this ISO datetime."),
    updatedAtGte: z.iso
      .datetime({ offset: true })
      .optional()
      .describe("Only assets updated at or after this ISO datetime."),
    updatedAtLte: z.iso
      .datetime({ offset: true })
      .optional()
      .describe("Only assets updated at or before this ISO datetime."),
    orderBy: assetOrderBySchema
      .optional()
      .describe(
        "Sort order when listing (ignored when q is set). Prefix a field with '-' for descending. Defaults to -createdAt (newest first).",
      ),
    limit: z
      .number()
      .int()
      .positive()
      .max(50)
      .default(50)
      .optional()
      .describe("Maximum number of assets to return. Defaults to 50."),
  })
  .describe("McpFindAssetsInput");
export const mcpFindAssetsOutputSchema = assetFindResponseSchema;
export const mcpUpdateAssetInputSchema = assetUpdateRequestSchema;
export const mcpUpdateAssetOutputSchema = assetUpdateResponseSchema;

const mcpFirecrawlBrandingFontSchema = z.object({
  family: z.string(),
});

const mcpFirecrawlBrandingScalarSchema = z
  .string()
  .or(z.number())
  .or(z.boolean())
  .or(z.null())
  .or(z.record(z.string(), z.unknown()));

// Firecrawl returns `null` for missing entries inside otherwise-string records
// (e.g. `branding.images.favicon = null` when a page has no favicon). Schemas
// here mirror that with `.nullable()` value types so real responses don't
// fail validation. The downstream `getBranding` wrapper filters non-strings
// out before use, so widening the schema is purely a parse-correctness fix.
const mcpFirecrawlBrandingSchema = z.object({
  colorScheme: z.enum(["light", "dark"]).optional(),
  logo: z.string().nullish(),
  colors: z.record(z.string(), z.string().nullable()).nullish(),
  fonts: z.array(mcpFirecrawlBrandingFontSchema).nullish(),
  typography: z
    .object({
      fontFamilies: z.record(z.string(), z.string().nullable()).optional(),
      fontSizes: z.record(z.string(), z.string().nullable()).optional(),
      fontWeights: z.record(z.string(), z.number().nullable()).optional(),
      lineHeights: z.record(z.string(), z.string().nullable()).optional(),
    })
    .nullish(),
  spacing: z
    .object({
      baseUnit: z.number().int().optional(),
      borderRadius: z.string().optional(),
      padding: z
        .record(z.string(), mcpFirecrawlBrandingScalarSchema)
        .optional(),
      margins: z
        .record(z.string(), mcpFirecrawlBrandingScalarSchema)
        .optional(),
    })
    .nullish(),
  components: z
    .record(z.string(), z.record(z.string(), mcpFirecrawlBrandingScalarSchema))
    .nullish(),
  icons: z
    .record(z.string(), z.record(z.string(), mcpFirecrawlBrandingScalarSchema))
    .nullish(),
  images: z.record(z.string(), z.string().nullable()).nullish(),
  animations: z.record(z.string(), mcpFirecrawlBrandingScalarSchema).nullish(),
  layout: z.record(z.string(), mcpFirecrawlBrandingScalarSchema).nullish(),
  personality: z.record(z.string(), mcpFirecrawlBrandingScalarSchema).nullish(),
});

export const mcpScrapePageInputSchema = z
  .object({
    url: z.url(),
    maxAge: z.number().int().nonnegative().optional(),
  })
  .describe("McpScrapePageInput");

export const mcpScrapePageOutputSchema = z
  .object({
    markdown: z.string().optional(),
    rawHtml: z.string().nullish(),
    screenshot: z.string().nullish(),
    images: z.array(z.string()).optional(),
    branding: mcpFirecrawlBrandingSchema.nullish(),
    metadata: z
      .object({
        title: z.string().or(z.array(z.string())).optional(),
        description: z.string().or(z.array(z.string())).optional(),
        ogTitle: z.string().or(z.array(z.string())).optional(),
        ogDescription: z.string().or(z.array(z.string())).optional(),
        ogImage: z.string().or(z.array(z.string())).optional(),
        language: z.string().or(z.array(z.string())).nullish(),
        sourceURL: z.string().optional(),
        url: z.string().optional(),
        keywords: z.string().or(z.array(z.string())).optional(),
        statusCode: z.number().optional(),
        contentType: z.string().optional(),
        error: z.string().nullish(),
        concurrencyLimited: z.boolean().optional(),
        concurrencyQueueDurationMs: z.number().optional(),
      })
      .optional(),
    sourceUrl: z.string().optional(),
  })
  .passthrough()
  .describe("McpScrapePageOutput");

export const mcpResolvedLegacyV2ProductSchema = z
  .object({
    externalId: z.string().nullish(),
    handle: z.string().optional(),
    referenceType: z.string().optional(),
    reploId: z.string(),
    status: z.enum(["not_found", "resolved"]),
    title: z.string().optional(),
  })
  .describe("McpResolvedLegacyV2Product");

export const mcpResolveLegacyV2ProductIdsInputSchema = z
  .object({
    productIds: z
      .array(z.uuid())
      .min(1)
      .max(100)
      .describe("Array of Replo product UUIDs to resolve"),
  })
  .describe("McpResolveLegacyV2ProductIdsInput");

export const mcpResolveLegacyV2ProductIdsOutputSchema = z
  .object({
    products: z.array(mcpResolvedLegacyV2ProductSchema),
  })
  .describe("McpResolveLegacyV2ProductIdsOutput");

export const mcpListIntegrationsInputSchema = z
  .object({
    tag: integrationTagSchema
      .optional()
      .describe("Filter to integrations matching this tag. Omit to list all."),
  })
  .describe("McpListIntegrationsInput");

const mcpIntegrationConnectionSchema = z.object({
  connectionId: integrationConnectionIdSchema,
  connectionScope: integrationScopeKeySchema,
  connectionConfig: z
    .record(z.string(), z.string())
    .optional()
    .describe(
      "Stored non-secret connection settings. Secret credentials are never returned.",
    ),
  // True when the connection is stored but was rejected upstream and needs to
  // be reconnected before its tools work again (REPL-29041).
  needsReconnect: z.boolean().optional(),
});

export const mcpListIntegrationsOutputSchema = z
  .object({
    integrations: z.array(
      z.object({
        allowedScopes: integrationAllowedScopesSchema,
        connections: z.array(mcpIntegrationConnectionSchema),
        description: z.string(),
        image: z.string().optional(),
        integrationKey: z.string(),
        isBuiltIn: z
          .boolean()
          .optional()
          .describe(
            "A built-in Replo capability that is always available and has nothing to connect.",
          ),
        name: z.string(),
        tags: z.array(integrationTagSchema),
        usageGuidance: z.string(),
      }),
    ),
  })
  .describe("McpListIntegrationsOutput");

export const mcpShowIntegrationStatusInputSchema = z
  .object({
    integrationKey: z
      .string()
      .describe(
        "The key of the integration to show status for (e.g., 'google-calendar', 'google-drive', 'notion')",
      ),
  })
  .describe("McpShowIntegrationStatusInput");

export const mcpShowIntegrationStatusOutputSchema = z
  .object({
    allowedScopes: integrationAllowedScopesSchema.optional(),
    connections: z.array(mcpIntegrationConnectionSchema).optional(),
    // NOTE (Max, 2026-08-28): "replo-products" is read-only legacy — the server
    // stopped emitting it, but this schema also parses tool output persisted in
    // old sessions, which would otherwise fail to render.
    connectionUi: z
      .enum([
        "nango-oauth",
        "nango-connect-ui",
        "custom-api-key",
        "shopify-app-install",
        "replo-products",
      ])
      .optional(),
    connectionHighlights: z.array(z.string()).optional(),
    description: z.string().optional(),
    error: z.boolean().optional(),
    fields: z
      .array(
        z.object({
          key: z.string(),
          label: z.string(),
          required: z.boolean(),
          type: z.enum(["text", "password"]),
        }),
      )
      .optional(),
    image: z.string().optional(),
    integrationKey: z.string().optional(),
    isBuiltIn: z
      .boolean()
      .optional()
      .describe(
        "A built-in Replo capability that is always available and has nothing to connect.",
      ),
    message: z.string().optional(),
    name: z.string().optional(),
    tags: z.array(integrationTagSchema).optional(),
    usageGuidance: z.string().optional(),
  })
  .describe("McpShowIntegrationStatusOutput");

// --- Site CRUD schemas ---

const mcpSiteOutputSchema = z.object({
  id: z.uuid(),
  subdomain: z.string(),
  name: z.string(),
  isDefault: z
    .boolean()
    .describe(
      "Whether this is the project's default site, used when the user does not name one.",
    ),
  createdAt: z.string(),
});

export const mcpContentReadEntryInputSchema =
  contentReadEntryInputSchema.describe("McpContentReadEntryInput");
export const mcpContentCreateEntryInputSchema =
  contentCreateEntryInputSchema.describe("McpContentCreateEntryInput");
export const mcpContentUpdateEntryInputSchema =
  contentUpdateEntryInputSchema.describe("McpContentUpdateEntryInput");
export const mcpContentArchiveEntryInputSchema =
  contentArchiveEntryInputSchema.describe("McpContentArchiveEntryInput");
export const mcpContentEntryCommandOutputSchema =
  contentEntryCommandOutputSchema.describe("McpContentEntryCommandOutput");

export const mcpListSitesInputSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Optional display-name filter. When provided, returns only sites whose name matches. Omit to list all sites.",
      ),
  })
  .describe("McpListSitesInput");

export const mcpListSitesOutputSchema = z
  .object({
    sites: z.array(mcpSiteOutputSchema),
  })
  .describe("McpListSitesOutput");

export const mcpCreateSiteInputSchema = z
  .object({
    name: z
      .string()
      .min(1)
      .max(255)
      .optional()
      .describe(
        "Optional human-readable display name (e.g. the template or brand). Defaults to 'Website' and is auto-incremented on collision ('Brex', 'Brex 2'). The on-disk folder is derived from this name at provision time.",
      ),
    subdomain: z
      .string()
      .min(1)
      .max(63)
      .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, {
        message:
          "Subdomain must be lowercase alphanumeric with hyphens, and cannot start or end with a hyphen.",
      })
      .optional()
      .describe(
        "Optional subdomain for the new site (e.g. 'my-store-blog'). Defaults to an opaque id-based subdomain when omitted. Must be globally unique.",
      ),
  })
  .describe("McpCreateSiteInput");

export const mcpCreateSiteOutputSchema = z
  .object({
    site: mcpSiteOutputSchema,
  })
  .describe("McpCreateSiteOutput");

export const mcpProvisionSiteInputSchema = z
  .object({
    siteId: z
      .uuid()
      .optional()
      .describe(
        "The ID of the site to provision onto this sandbox. Pass an existing site's ID (from list_sites) to clone and install a site that already exists. Omit to create a brand-new site — a fresh ID is generated for you, so never invent one. For a new site, `subdomain` and `name` are both optional and default sensibly when omitted.",
      ),
    subdomain: z
      .string()
      .min(1)
      .max(63)
      .regex(/^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/, {
        message:
          "Subdomain must be lowercase alphanumeric with hyphens, and cannot start or end with a hyphen.",
      })
      .optional()
      .describe(
        "Optional subdomain for a new site (e.g. 'my-store-blog'). Defaults to an opaque id-based subdomain when omitted. Must be globally unique; if you pass one that's taken, provisioning fails with a 'subdomain already taken' error.",
      ),
    name: z
      .string()
      .min(1)
      .max(255)
      .optional()
      .describe(
        "Optional human-readable display name for a new site (e.g. the template name). Defaults to 'Website' and is auto-incremented on collision ('Brex', 'Brex 2', 'Brex 3').",
      ),
    template: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Optional starter template to fork for a new site. Defaults to a blank site when omitted.",
      ),
    gitRemote: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Optional git remote (an Artifacts repo URL or bare repo name in the sandbox's tier/namespace) to fork the new site from. Takes precedence over `template`. The new site still gets its own repo named by its siteId. Provisioning fails with a 'not available' error if the remote doesn't resolve to a repo in the same namespace.",
      ),
  })
  .describe("McpProvisionSiteInput");

export const mcpProvisionSiteOutputSchema = z
  .object({
    site: mcpSiteOutputSchema,
    provisioned: z
      .boolean()
      .describe(
        "True once the site is cloned, dependencies are installed, and its dev server is serving the live preview.",
      ),
  })
  .describe("McpProvisionSiteOutput");

export const mcpDeleteSiteInputSchema = z
  .object({
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to delete. Use list_sites to find site IDs.",
      ),
  })
  .describe("McpDeleteSiteInput");

export const mcpDeleteSiteOutputSchema = z
  .object({
    deleted: z.boolean(),
  })
  .describe("McpDeleteSiteOutput");

export const mcpUpdateSiteInputSchema = z
  .object({
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to rename. Use list_sites to find site IDs.",
      ),
    name: z
      .string()
      .min(1)
      .max(255)
      .describe(
        "The new human-readable display name for the site. This is the site's name shown in the dashboard, distinct from the SEO page title in the site's layout metadata.",
      ),
  })
  .describe("McpUpdateSiteInput");

export const mcpUpdateSiteOutputSchema = z
  .object({
    site: mcpSiteOutputSchema,
  })
  .describe("McpUpdateSiteOutput");

// --- Membership management tool schemas ---
//
// Project memberships and workspace memberships are managed separately. The
// agent should default to project membership and only touch workspace
// membership when the user explicitly asks about their workspace.

const mcpProjectMembershipSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
});

export const mcpListProjectMembershipsInputSchema = z
  .object({})
  .describe("McpListProjectMembershipsInput");

export const mcpProjectMembershipsOutputSchema = z
  .object({
    memberships: z.array(mcpProjectMembershipSchema),
  })
  .describe("McpProjectMembershipsOutput");

export const mcpAddProjectMembershipOutputSchema =
  mcpProjectMembershipsOutputSchema
    .extend({
      failedEmails: z
        .array(z.email())
        .describe(
          "Emails that could not be added (e.g. deactivated accounts). When non-empty, tell the user which addresses failed; everyone else was added successfully.",
        ),
    })
    .describe("McpAddProjectMembershipOutput");

export const mcpAddProjectMembershipInputSchema = z
  .object({
    emails: z
      .array(z.email())
      .min(1)
      .max(20)
      .describe(
        "Email addresses to add to the current project. People not yet registered with Replo are invited by email.",
      ),
  })
  .describe("McpAddProjectMembershipInput");

export const mcpRemoveProjectMembershipInputSchema = z
  .object({
    userId: z
      .uuid()
      .describe(
        "The userId of the person whose project membership to remove. Get this from list_project_memberships.",
      ),
  })
  .describe("McpRemoveProjectMembershipInput");

const mcpWorkspaceMembershipSchema = z.object({
  userId: z.string(),
  email: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  role: userWorkspaceRoleSchema.nullable(),
});

export const mcpListWorkspaceMembershipsInputSchema = z
  .object({})
  .describe("McpListWorkspaceMembershipsInput");

export const mcpWorkspaceMembershipsOutputSchema = z
  .object({
    memberships: z.array(mcpWorkspaceMembershipSchema),
  })
  .describe("McpWorkspaceMembershipsOutput");

export const mcpAddWorkspaceMembershipOutputSchema =
  mcpWorkspaceMembershipsOutputSchema
    .extend({
      failedEmails: z
        .array(z.email())
        .describe(
          "Emails that could not be added (e.g. deactivated accounts). When non-empty, tell the user which addresses failed; everyone else was added successfully.",
        ),
    })
    .describe("McpAddWorkspaceMembershipOutput");

export const mcpAddWorkspaceMembershipInputSchema = z
  .object({
    emails: z
      .array(z.email())
      .min(1)
      .max(20)
      .describe(
        "Email addresses to add to the workspace. People not yet registered with Replo are invited by email.",
      ),
    role: userWorkspaceRoleSchema
      .default("member")
      .describe(
        "The workspace role to grant. Defaults to 'member'. Use 'owner' only when the user explicitly asks to make someone an admin/owner.",
      ),
  })
  .describe("McpAddWorkspaceMembershipInput");

export const mcpRemoveWorkspaceMembershipInputSchema = z
  .object({
    userId: z
      .uuid()
      .describe(
        "The userId of the person whose workspace membership to remove. Get this from list_workspace_memberships.",
      ),
  })
  .describe("McpRemoveWorkspaceMembershipInput");

// --- Account / project / workspace update tool schemas ---

export const mcpUpdateAccountInputSchema = z
  .object({
    firstName: z
      .string()
      .trim()
      .min(1)
      .max(100)
      .describe("The user's first name."),
    lastName: z
      .string()
      .trim()
      .max(100)
      .describe(
        "The user's last name. Required — pass an empty string for mononyms. Updating the account always writes both names, so omitting this would clear it.",
      ),
  })
  .describe("McpUpdateAccountInput");

export const mcpUpdateAccountOutputSchema = z
  .object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
  })
  .describe("McpUpdateAccountOutput");

export const mcpUpdateProjectInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(255)
      .optional()
      .describe("The new name for the current project."),
    timeZone: z
      .string()
      .min(1)
      .optional()
      .describe(
        "The project's IANA time zone (for example 'America/New_York').",
      ),
    currency: currencySchema
      .optional()
      .describe("The project's default currency."),
    supportEmail: z
      .email()
      .nullish()
      .describe(
        "The store's support email — the Reply-To on buyer order emails. " +
          "Only set an address the user explicitly provided for customer " +
          "support; never their login email. Null clears it.",
      ),
  })
  .describe("McpUpdateProjectInput");

export const mcpUpdateProjectOutputSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
  })
  .describe("McpUpdateProjectOutput");

export const mcpUpdateWorkspaceInputSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1)
      .max(255)
      .describe("The new name for the project's workspace."),
  })
  .describe("McpUpdateWorkspaceInput");

export const mcpUpdateWorkspaceOutputSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .describe("McpUpdateWorkspaceOutput");

export const mcpGetAccountInputSchema = z
  .object({})
  .describe("McpGetAccountInput");

export const mcpGetAccountOutputSchema = z
  .object({
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    name: z.string().nullable(),
    email: z.string(),
  })
  .describe("McpGetAccountOutput");

export const mcpGetProjectInputSchema = z
  .object({})
  .describe("McpGetProjectInput");

export const mcpGetProjectOutputSchema = z
  .object({
    id: z.string(),
    name: z.string().nullable(),
    shopifyUrl: z.string().nullable(),
    onboardingContext: z.enum(["blank", "named-project", "connected-store"]),
  })
  .describe("McpGetProjectOutput");

export const mcpGetWorkspaceInputSchema = z
  .object({})
  .describe("McpGetWorkspaceInput");

export const mcpGetWorkspaceOutputSchema = z
  .object({
    id: z.string(),
    name: z.string(),
  })
  .describe("McpGetWorkspaceOutput");

// --- Custom Domain tool schemas ---
//
// The input schemas are defined here because they're only ever used to register
// these MCP tools. The output schemas live in `schemas/customDomain` because
// their generated types are also consumed by the bedrock SDK, which must not
// import from MCP-specific code.

export const mcpAddCustomDomainInputSchema = z
  .object({
    domain: z
      .string()
      .min(1)
      .max(253)
      .regex(
        /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/,
        "Domain must be lowercase with no scheme, path, or trailing dot (e.g. 'shop.example.com')",
      )
      .describe(
        "The custom domain to add, lowercase (e.g. 'shop.example.com' or 'example.com')",
      ),
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to attach this domain to. Get this from list_sites.",
      ),
  })
  .describe("McpAddCustomDomainInput");
export const mcpAddCustomDomainOutputSchema = addCustomDomainOutputSchema;

export const mcpFindCustomDomainsInputSchema = z
  .object({
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to list domains for. Get this from list_sites.",
      ),
  })
  .describe("McpFindCustomDomainsInput");
export const mcpFindCustomDomainsOutputSchema = findCustomDomainsOutputSchema;

export const mcpGetCustomDomainInputSchema = z
  .object({
    customDomainId: z
      .string()
      .describe("The ID of the custom domain to retrieve"),
  })
  .describe("McpGetCustomDomainInput");
export const mcpGetCustomDomainOutputSchema = getCustomDomainOutputSchema;

export const mcpDeleteCustomDomainInputSchema = z
  .object({
    customDomainId: z
      .string()
      .describe("The ID of the custom domain to delete"),
  })
  .describe("McpDeleteCustomDomainInput");
export const mcpDeleteCustomDomainOutputSchema = deleteCustomDomainOutputSchema;

export const mcpVerifyCustomDomainInputSchema = z
  .object({
    customDomainId: z
      .string()
      .describe("The ID of the pending custom domain to verify and activate"),
  })
  .describe("McpVerifyCustomDomainInput");
export const mcpVerifyCustomDomainOutputSchema = verifyCustomDomainOutputSchema;

export const knownReploMcpToolMetadataRegistrySchema = z
  .object({
    list_sites: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpListSitesInputSchema,
      output: mcpListSitesOutputSchema,
    }),
    create_site: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpCreateSiteInputSchema,
      output: mcpCreateSiteOutputSchema,
    }),
    provision_site: z.object({
      iconKey: z.literal("bedrock"),
      input: mcpProvisionSiteInputSchema,
      output: mcpProvisionSiteOutputSchema,
    }),
    content_read_entry: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpContentReadEntryInputSchema,
      output: mcpContentEntryCommandOutputSchema,
    }),
    content_create_entry: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpContentCreateEntryInputSchema,
      output: mcpContentEntryCommandOutputSchema,
    }),
    content_update_entry: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpContentUpdateEntryInputSchema,
      output: mcpContentEntryCommandOutputSchema,
    }),
    content_archive_entry: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpContentArchiveEntryInputSchema,
      output: mcpContentEntryCommandOutputSchema,
    }),
    delete_site: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpDeleteSiteInputSchema,
      output: mcpDeleteSiteOutputSchema,
    }),
    list_project_memberships: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpListProjectMembershipsInputSchema,
      output: mcpProjectMembershipsOutputSchema,
    }),
    add_project_membership: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpAddProjectMembershipInputSchema,
      output: mcpAddProjectMembershipOutputSchema,
    }),
    remove_project_membership: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpRemoveProjectMembershipInputSchema,
      output: mcpProjectMembershipsOutputSchema,
    }),
    list_workspace_memberships: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpListWorkspaceMembershipsInputSchema,
      output: mcpWorkspaceMembershipsOutputSchema,
    }),
    add_workspace_membership: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpAddWorkspaceMembershipInputSchema,
      output: mcpAddWorkspaceMembershipOutputSchema,
    }),
    remove_workspace_membership: z.object({
      iconKey: z.literal("bedrockMembers"),
      input: mcpRemoveWorkspaceMembershipInputSchema,
      output: mcpWorkspaceMembershipsOutputSchema,
    }),
    add_custom_domain: z.object({
      iconKey: z.literal("bedrockDomains"),
      input: mcpAddCustomDomainInputSchema,
      output: mcpAddCustomDomainOutputSchema,
    }),
    delete_custom_domain: z.object({
      iconKey: z.literal("bedrockDomains"),
      input: mcpDeleteCustomDomainInputSchema,
      output: mcpDeleteCustomDomainOutputSchema,
    }),
    find_custom_domains: z.object({
      iconKey: z.literal("bedrockDomains"),
      input: mcpFindCustomDomainsInputSchema,
      output: mcpFindCustomDomainsOutputSchema,
    }),
    get_custom_domain: z.object({
      iconKey: z.literal("bedrockDomains"),
      input: mcpGetCustomDomainInputSchema,
      output: mcpGetCustomDomainOutputSchema,
    }),
    verify_custom_domain: z.object({
      iconKey: z.literal("bedrockDomains"),
      input: mcpVerifyCustomDomainInputSchema,
      output: mcpVerifyCustomDomainOutputSchema,
    }),
    scrape_page: z.object({
      iconKey: z.literal("bedrockSites"),
      input: mcpScrapePageInputSchema,
      output: mcpScrapePageOutputSchema,
    }),
    find_assets: z.object({
      iconKey: z.literal("bedrockAssets"),
      input: mcpFindAssetsInputSchema,
      output: mcpFindAssetsOutputSchema,
    }),
    list_integrations: z.object({
      iconKey: z.literal("bedrockIntegrations"),
      input: mcpListIntegrationsInputSchema,
      output: mcpListIntegrationsOutputSchema,
    }),
    resolve_legacy_v2_product_ids: z.object({
      iconKey: z.literal("bedrockProducts"),
      input: mcpResolveLegacyV2ProductIdsInputSchema,
      output: mcpResolveLegacyV2ProductIdsOutputSchema,
    }),
    show_integration_status: z.object({
      iconKey: z.literal("bedrockIntegrations"),
      input: mcpShowIntegrationStatusInputSchema,
      output: mcpShowIntegrationStatusOutputSchema,
    }),
    upload_assets: z.object({
      iconKey: z.literal("bedrockAssets"),
      input: mcpUploadAssetsInputSchema,
      output: mcpUploadAssetsJobOutputSchema,
    }),
    download_assets: z.object({
      iconKey: z.literal("bedrockAssets"),
      input: mcpDownloadAssetsInputSchema,
      output: mcpDownloadAssetsOutputSchema,
    }),
    get_asset_download_status: z.object({
      iconKey: z.literal("bedrockAssets"),
      input: mcpGetAssetDownloadStatusInputSchema,
      output: mcpGetAssetDownloadStatusOutputSchema,
    }),
    get_asset_upload_status: z.object({
      iconKey: z.literal("bedrockAssets"),
      input: mcpGetAssetUploadStatusInputSchema,
      output: mcpGetAssetUploadStatusOutputSchema,
    }),
    get_account: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpGetAccountInputSchema,
      output: mcpGetAccountOutputSchema,
    }),
    get_project: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpGetProjectInputSchema,
      output: mcpGetProjectOutputSchema,
    }),
    get_workspace: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpGetWorkspaceInputSchema,
      output: mcpGetWorkspaceOutputSchema,
    }),
    update_account: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpUpdateAccountInputSchema,
      output: mcpUpdateAccountOutputSchema,
    }),
    update_project: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpUpdateProjectInputSchema,
      output: mcpUpdateProjectOutputSchema,
    }),
    update_workspace: z.object({
      iconKey: z.literal("bedrockSettings"),
      input: mcpUpdateWorkspaceInputSchema,
      output: mcpUpdateWorkspaceOutputSchema,
    }),
  })
  .describe("KnownReploMcpToolMetadataRegistry");

export const bedrockMcpCreateTaskInputSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1)
      .max(255)
      .describe("Short human-readable title for the task."),
    emoji: z
      .string()
      .trim()
      .min(1)
      .max(8)
      .default("✨")
      .describe(
        "Emoji label used to visually group this task in the schedule UI.",
      ),
    prompt: z
      .string()
      .trim()
      .min(1)
      .describe(
        "The exact prompt the task should run. This must be self-contained and executable without asking the user follow-up questions. Include any reasonable assumptions needed to complete the task, and do not include instructions like 'ask the user' or 'wait for clarification'.",
      ),
    scheduledAt: z.iso
      .datetime({ offset: true })
      .describe(
        "ISO datetime with explicit timezone offset when the task should first run. Must be in the future. Interpret relative dates/times using the current time authority from the system prompt. Do not use UTC unless the system prompt explicitly states it",
      ),
    rrule: z
      .string()
      .trim()
      .min(1)
      .default("FREQ=DAILY;COUNT=1")
      .describe(
        "RRULE string for recurrence, such as FREQ=DAILY. Use FREQ=DAILY;COUNT=1 for one-time tasks. Bedrock stores this with a DTSTART derived from scheduledAt.",
      ),
  })
  .describe("BedrockMcpCreateTaskInput");

export const bedrockMcpCreateTaskOutputSchema = z
  .object({
    task: taskSchema,
  })
  .describe("BedrockMcpCreateTaskOutput");

export const bedrockMcpUpdateTaskInputSchema = bedrockMcpCreateTaskInputSchema
  .extend({
    taskId: z.uuid().describe("ID of the scheduled task to update."),
    // Updates fully replace the task definition, so emoji and rrule must be
    // supplied explicitly. Inheriting create's `.default()` would mark them
    // optional and silently reset omitted values (e.g. turning a recurring
    // task into a one-time one). Read the task's current values via list_tasks
    // first and pass them through unchanged when not editing them.
    emoji: z
      .string()
      .trim()
      .min(1)
      .max(8)
      .describe(
        "Emoji label used to visually group this task in the schedule UI.",
      ),
    rrule: z
      .string()
      .trim()
      .min(1)
      .describe(
        "RRULE string for recurrence, such as FREQ=DAILY. Use FREQ=DAILY;COUNT=1 for one-time tasks. This replaces the task's existing recurrence, so pass the current value unchanged unless the user is rescheduling.",
      ),
  })
  .describe("BedrockMcpUpdateTaskInput");

export const bedrockMcpUpdateTaskOutputSchema = z
  .object({
    task: taskSchema,
  })
  .describe("BedrockMcpUpdateTaskOutput");

export const bedrockMcpListTasksInputSchema = z
  .object({})
  .describe("BedrockMcpListTasksInput");

export const bedrockMcpListTasksOutputSchema = z
  .object({
    tasks: z.array(taskSchema),
  })
  .describe("BedrockMcpListTasksOutput");

export const bedrockMcpDeleteTaskInputSchema = z
  .object({
    taskId: z.uuid().describe("ID of the scheduled task to delete."),
  })
  .describe("BedrockMcpDeleteTaskInput");

export const bedrockMcpDeleteTaskOutputSchema = z
  .object({
    deleted: z.literal(true),
    task: taskSchema,
  })
  .describe("BedrockMcpDeleteTaskOutput");

export const registryFindItemsInputSchema = z
  .object({
    type: registryItemTypeSchema,
    search: z
      .string()
      .min(1)
      .max(200)
      .optional()
      .describe(
        "Optional substring matched against item name and description (case-insensitive).",
      ),
    category: z.string().min(1).max(120).optional(),
    tags: z.array(z.string().min(1).max(60)).max(20).optional(),
    cursor: z.string().min(1).max(200).optional(),
    limit: z.number().int().min(1).max(100).optional(),
  })
  .describe("RegistryFindItemsInput");

export const registryGetItemInputSchema = z
  .object({
    slug: registrySlugSchema,
    type: registryItemTypeSchema,
  })
  .describe("RegistryGetItemInput");

export const registryFindItemsOutputSchema =
  registryListItemsResultSchema.describe("RegistryFindItemsOutput");

export const registryGetItemOutputSchema = z
  .object({
    files: registryContentFilesSchema,
    dependencies: z.array(z.string()),
  })
  .describe("RegistryGetItemOutput");

export const orderIdInputSchema = z
  .object({
    orderId: z.uuid(),
  })
  .describe("OrdersOrderIdInput");

export const fulfillmentOrderIdInputSchema = z
  .object({
    fulfillmentOrderId: z.uuid(),
  })
  .describe("OrdersFulfillmentOrderIdInput");

export const ordersFindInputSchema = z
  .object({
    limit: z.number().int().min(1).max(200).optional(),
    cursor: z.string().optional(),
    financialStatus: orderFinancialStatusSchema.optional(),
    fulfillmentStatus: orderItemFulfillmentStatusSchema.optional(),
    customerEmail: z.string().optional(),
  })
  .describe("OrdersFindInput");

export const ordersListEventsInputSchema = orderIdInputSchema
  .extend({
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(200).optional(),
  })
  .describe("OrdersListEventsInput");

export const ordersAddNoteInputSchema = orderIdInputSchema
  .extend({
    markdown: z.string().min(1),
    fulfillmentOrderId: z.uuid().optional(),
    fulfillmentId: z.uuid().optional(),
  })
  .describe("OrdersAddNoteInput");

// A note's `eventId` is globally unique and resolves to exactly one order, so
// these don't take an `orderId` — unlike the other order-scoped tools.
export const ordersEditNoteInputSchema = z
  .object({
    eventId: z.uuid(),
    markdown: z.string().min(1),
  })
  .describe("OrdersEditNoteInput");

export const ordersDeleteNoteInputSchema = z
  .object({
    eventId: z.uuid(),
  })
  .describe("OrdersDeleteNoteInput");

export const ordersMarkFulfilledInputSchema = z
  .object({
    fulfillmentOrderId: z.uuid(),
    items: z
      .array(
        z.object({
          orderItemId: z.uuid(),
          quantity: z.number().int().positive().optional(),
        }),
      )
      .min(1)
      .optional()
      .describe(
        "Every item in the physical shipment. Omit quantity to fulfill everything remaining for the item; omit items entirely to fulfill everything remaining on the fulfillment order.",
      ),
    trackingNumber: z.string().optional(),
    trackingUrl: z.string().optional(),
    trackingCarrier: z.string().optional(),
  })
  .describe("OrdersMarkFulfilledInput");

export const ordersRefundInputSchema = orderIdInputSchema
  .extend({
    amount: amountSchema.positive().optional(),
    lineItemIds: z.array(z.uuid()).optional(),
    reason: z
      .enum(["duplicate", "fraudulent", "requested_by_customer"])
      .optional(),
  })
  .describe("OrdersRefundInput");

const registryItemIdSchema = z.uuid();

const assetProjectScopeSchema = z
  .object({
    kind: z.literal("project"),
    projectId: z.uuid(),
  })
  .describe("AssetProjectScope");

const assetRegistryScopeSchema = z
  .object({
    kind: z.literal("registry"),
    registryItemId: registryItemIdSchema,
  })
  .describe("AssetRegistryScope");

export const assetScopeSchema = z
  .discriminatedUnion("kind", [
    assetProjectScopeSchema,
    assetRegistryScopeSchema,
  ])
  .describe("AssetScope");

export const assetPresignInputSchema = z
  .object({
    scope: assetScopeSchema,
    assets: z
      .array(
        z.object({
          contentType: z.string().min(1),
          contentLength: z.number().int().positive().max(1_073_741_824),
        }),
      )
      .min(1)
      .max(100),
  })
  .describe("AssetPresignInput");

export const assetPresignOutputSchema = z
  .object({
    assets: z.array(
      z.object({
        objectKey: z.string().min(1),
        presignedPutUrl: z.url(),
        publicUrl: z.url(),
      }),
    ),
  })
  .describe("AssetPresignOutput");

// Finalize a presigned upload (bytes already at objectKey): record the row, reading images back for AI.
export const assetCommitInputSchema = z
  .object({
    // Caller-assigned id so the optimistic tile and persisted asset share one identity. Omit to let the server mint one.
    id: z.uuid().optional(),
    objectKey: z.string().min(1).max(512),
    name: z.string().min(1).optional(),
    mimeType: z.string().min(1).optional(),
    // Client-known size; authoritative for non-images (which are never read back).
    sizeBytes: z.number().int().nonnegative().optional(),
    generateEmbedding: z.boolean().optional(),
  })
  .describe("AssetCommitInput");

export const assetCommitOutputSchema = assetUploadSuccessSchema
  .omit({ index: true })
  .describe("AssetCommitOutput");

// Google PageSpeed Insights (`pagespeed_analyze`) is a Replo-owned,
// gateway-backed first-party tool, so its schemas live inline here alongside
// the other bedrock tools rather than in a separate module. The output is a
// trimmed shape mapped from the PageSpeed Insights response, never the raw
// Lighthouse payload. It keeps lab (Lighthouse) and field (CrUX) data separate
// so callers never conflate the two.
const pagespeedStrategySchema = z.enum(["mobile", "desktop"]);
const pagespeedRequestStrategySchema = z.enum(["mobile", "desktop", "both"]);
const pagespeedCategorySchema = z.enum([
  "performance",
  "accessibility",
  "best-practices",
  "seo",
  "agentic-browsing",
]);

export const pagespeedMcpAnalyzeInputSchema = z
  .object({
    url: z
      .string()
      .min(1)
      .describe("The full URL to audit, including the https:// scheme."),
    strategy: pagespeedRequestStrategySchema
      .optional()
      .describe(
        'Device profile to emulate. "both" runs mobile and desktop and returns one result per device. Defaults to "both".',
      ),
    categories: z
      .array(pagespeedCategorySchema)
      .optional()
      .describe('Lighthouse categories to run. Defaults to ["performance"].'),
  })
  .describe("PagespeedMcpAnalyzeInput");

const pagespeedOpportunitySchema = z.object({
  id: z.string(),
  title: z.string(),
  estimatedSavingsMs: z.number().nullable(),
});

// Lighthouse lab (synthetic) metrics. Lab runs cannot measure INP, so we expose
// Total Blocking Time as the lab proxy and describe the gap in the note field.
const pagespeedLabDataSchema = z.object({
  largestContentfulPaintMs: z.number().nullable(),
  cumulativeLayoutShift: z.number().nullable(),
  firstContentfulPaintMs: z.number().nullable(),
  timeToFirstByteMs: z.number().nullable(),
  totalBlockingTimeMs: z.number().nullable(),
  speedIndexMs: z.number().nullable(),
  interactionToNextPaintNote: z.string(),
});

const pagespeedFieldDistributionBinSchema = z.object({
  min: z.number().nullable(),
  max: z.number().nullable(),
  proportion: z.number().nullable(),
});

// A single CrUX field metric: the 75th-percentile value, its good/needs-
// improvement/poor rating, and the fast/average/slow distribution buckets.
const pagespeedFieldMetricSchema = z.object({
  percentile: z.number().nullable(),
  category: z.string().nullable(),
  distributions: z.array(pagespeedFieldDistributionBinSchema),
});

const pagespeedFieldMetricsSchema = z.object({
  largestContentfulPaint: pagespeedFieldMetricSchema.nullable(),
  interactionToNextPaint: pagespeedFieldMetricSchema.nullable(),
  cumulativeLayoutShift: pagespeedFieldMetricSchema.nullable(),
  firstContentfulPaint: pagespeedFieldMetricSchema.nullable(),
  timeToFirstByte: pagespeedFieldMetricSchema.nullable(),
});

const pagespeedFieldRecordSchema = z.object({
  overallCategory: z.string().nullable(),
  originFallback: z.boolean(),
  metrics: pagespeedFieldMetricsSchema,
});

// Real-user field data. `url` is page-level CrUX, `origin` is the whole-site
// aggregate. `preferredSource` encodes the URL field > origin field > lab
// priority so callers know which record to trust first.
const pagespeedFieldDataSchema = z.object({
  url: pagespeedFieldRecordSchema.nullable(),
  origin: pagespeedFieldRecordSchema.nullable(),
  preferredSource: z.enum(["url", "origin", "lab"]),
});

// The agentic-browsing category is scored as a fraction (passed/total), not a
// 0-1 gauge, so it is kept out of `scores` and preserved with per-audit detail.
const pagespeedAgenticAuditSchema = z.object({
  id: z.string(),
  title: z.string(),
  passed: z.boolean().nullable(),
});

const pagespeedAgenticBrowsingSchema = z.object({
  passedAudits: z.number(),
  totalAudits: z.number(),
  audits: z.array(pagespeedAgenticAuditSchema),
});

const pagespeedRunResultSchema = z.object({
  strategy: pagespeedStrategySchema,
  fetchedUrl: z.string(),
  scores: z.record(z.string(), z.number()),
  agenticBrowsing: pagespeedAgenticBrowsingSchema.nullable(),
  lab: pagespeedLabDataSchema,
  field: pagespeedFieldDataSchema,
  topOpportunities: z.array(pagespeedOpportunitySchema),
});

export const pagespeedMcpAnalyzeOutputSchema = z
  .object({
    url: z.string(),
    results: z.array(pagespeedRunResultSchema),
  })
  .describe("PagespeedMcpAnalyzeOutput");

export const knownBedrockMcpToolMetadataRegistrySchema = z
  .object({
    bedrock_database_create_collection: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpCreateCollectionInputSchema,
      output: bedrockMcpCollectionEnvelopeOutputSchema,
    }),
    bedrock_database_create_collection_field: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpCreateCollectionFieldInputSchema,
      output: bedrockMcpCollectionEnvelopeOutputSchema,
    }),
    bedrock_database_create_record: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpCreateRecordInputSchema,
      output: bedrockMcpRecordEnvelopeOutputSchema,
    }),
    bedrock_database_delete_collection_field: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpDeleteCollectionFieldInputSchema,
      output: bedrockMcpCollectionEnvelopeOutputSchema,
    }),
    bedrock_database_delete_record: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpDeleteRecordInputSchema,
      output: bedrockMcpDeleteRecordOutputSchema,
    }),
    bedrock_database_get_collections: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpGetCatalogInputSchema,
      output: bedrockMcpCatalogOutputSchema,
    }),
    bedrock_database_query_records: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpQueryRecordsInputSchema,
      output: bedrockMcpRecordsEnvelopeOutputSchema,
    }),
    bedrock_database_read_record: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpReadRecordInputSchema,
      output: bedrockMcpRecordEnvelopeOutputSchema,
    }),
    bedrock_database_update_collection: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpUpdateCollectionInputSchema,
      output: bedrockMcpCollectionEnvelopeOutputSchema,
    }),
    bedrock_database_update_collection_field: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpUpdateCollectionFieldInputSchema,
      output: bedrockMcpCollectionEnvelopeOutputSchema,
    }),
    bedrock_database_update_record: z.object({
      iconKey: z.literal("bedrockDatabase"),
      input: bedrockMcpUpdateRecordInputSchema,
      output: bedrockMcpRecordEnvelopeOutputSchema,
    }),
    create_task: z.object({
      iconKey: z.literal("bedrockTasks"),
      input: bedrockMcpCreateTaskInputSchema,
      output: bedrockMcpCreateTaskOutputSchema,
    }),
    list_tasks: z.object({
      iconKey: z.literal("bedrockTasks"),
      input: bedrockMcpListTasksInputSchema,
      output: bedrockMcpListTasksOutputSchema,
    }),
    update_task: z.object({
      iconKey: z.literal("bedrockTasks"),
      input: bedrockMcpUpdateTaskInputSchema,
      output: bedrockMcpUpdateTaskOutputSchema,
    }),
    delete_task: z.object({
      iconKey: z.literal("bedrockTasks"),
      input: bedrockMcpDeleteTaskInputSchema,
      output: bedrockMcpDeleteTaskOutputSchema,
    }),
    replo_orders_find: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersFindInputSchema,
      output: orderListResponseSchema,
    }),
    replo_orders_get: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: orderIdInputSchema,
      output: orderDetailResponseSchema,
    }),
    replo_orders_events_list: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersListEventsInputSchema,
      output: orderEventListResponseSchema,
    }),
    replo_orders_notes_add: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersAddNoteInputSchema,
      output: z.object({ event: bedrockOrderEventSchema }),
    }),
    replo_orders_notes_edit: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersEditNoteInputSchema,
      output: z.object({ event: bedrockOrderEventSchema }),
    }),
    replo_orders_notes_delete: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersDeleteNoteInputSchema,
      output: z.object({ event: bedrockOrderEventSchema }),
    }),
    replo_orders_mark_fulfilled: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersMarkFulfilledInputSchema,
      output: fulfillmentResponseSchema,
    }),
    replo_orders_refund: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: ordersRefundInputSchema,
      output: refundResponseSchema,
    }),
    replo_fulfillment_orders_cancel: z.object({
      iconKey: z.literal("bedrockOrders"),
      input: fulfillmentOrderIdInputSchema,
      output: fulfillmentOrderResponseSchema,
    }),
    bedrock_generate_design_directions: z.object({
      iconKey: z.literal("mediaMcp"),
      input: designDirectionRequestSchema,
      output: mediaMcpDesignDirectionSetOutputSchema,
    }),
    bedrock_generate_image: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateImageInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    bedrock_generate_video: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateVideoInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    bedrock_get_generation_status: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGetGenerationStatusInputSchema,
      output: mediaMcpGetGenerationStatusOutputSchema,
    }),
    create_registry_item: z.object({
      iconKey: z.literal("bedrockRegistry"),
      input: registryCreateItemInputSchema,
      output: registryCreateItemOutputSchema,
    }),
    query_replo_analytics: z.object({
      iconKey: z.literal("bedrockAnalytics"),
      input: bedrockMcpQueryAnalyticsInputSchema,
      output: bedrockMcpQueryAnalyticsOutputSchema,
    }),
    registry_find_items: z.object({
      iconKey: z.literal("bedrockRegistry"),
      input: registryFindItemsInputSchema,
      output: registryFindItemsOutputSchema,
    }),
    registry_get_item: z.object({
      iconKey: z.literal("bedrockRegistry"),
      input: registryGetItemInputSchema,
      output: registryGetItemOutputSchema,
    }),
    pagespeed_analyze: z.object({
      iconKey: z.literal("bedrock"),
      input: pagespeedMcpAnalyzeInputSchema,
      output: pagespeedMcpAnalyzeOutputSchema,
    }),
    ...knownReploMcpToolMetadataRegistrySchema.shape,
    ...knownDataForSeoMcpToolMetadataRegistrySchema.shape,
  })
  .describe("KnownBedrockMcpToolMetadataRegistry");
